import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { createFileRoute, useParams } from "@tanstack/react-router";
import { Calendar } from "@untitledui/icons";
import { type ReactNode, useEffect, useMemo } from "react";
import { useInView } from "react-intersection-observer";
import {
	DetailField,
	DetailFieldGrid,
	DetailFieldSection,
} from "@/components/base/detail-field/detail-field";
import { EmptyValue } from "@/components/common/EmptyValue";
import type { HistoryEntry } from "@/components/Organisms/HistoryList";
import { HistoryList } from "@/components/Organisms/HistoryList";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { lifecycleQueryOptions } from "@/services/lifecycle/lifecycle.options";
import type { AuditLogDisplay } from "@/services/lifecycle/lifecycle.types";
import { getEntityAuditDisplayFields } from "@/utils/entity-audit-display";
import { useLifecycleDrawerContext } from "@/utils/lifecycle-detail/lifecycle-drawer-context";
import { formatLongDate } from "@/utils/utils";

export const Route = createFileRoute(
	"/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId/activity",
)({
	component: LifecycleActivityTabContent,
});

function LifecycleActivityTabContent() {
	const { t } = useAppTranslation();
	const { lifecycle } = useLifecycleDrawerContext();
	const params = useParams({
		from: "/_authenticated/(org-app)/_orgAdmin/org-admin/$orgId/configuration/lifecycles/$lifecycleId/activity",
	});
	const { createdAt, updatedAt, createdByUser, updatedByUser } =
		getEntityAuditDisplayFields(lifecycle);

	const lifecycleId = lifecycle.id;
	const organizationId = params.orgId;
	const pageSize = 10;

	const historyCountQuery = useQuery(
		lifecycleQueryOptions.history.count(lifecycleId, organizationId),
	);

	const { data, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
		useInfiniteQuery({
			...lifecycleQueryOptions.history.infinite(
				lifecycleId,
				pageSize,
				historyCountQuery.data ?? 100,
				organizationId,
			),
		});

	const historyEntries: HistoryEntry[] = useMemo(() => {
		if (!data?.pages) return [];
		return data.pages.flat().map(
			(log: AuditLogDisplay): HistoryEntry => ({
				id: log.id,
				action: log.action,
				date: log.date,
				fieldName: log.fieldName,
				oldValue: log.oldValue,
				newValue: log.newValue,
				user: log.user
					? {
							id: log.user.id,
							displayName: log.user.displayName,
							email: log.user.email,
							firstName: log.user.firstName,
							middleName: log.user.middleName,
							lastName: log.user.lastName,
						}
					: null,
				entityType: log.entityType,
			}),
		);
	}, [data]);

	const { ref: loadMoreRef, inView } = useInView({
		threshold: 0,
		rootMargin: "0px 0px 300px 0px",
	});

	useEffect(() => {
		if (inView && hasNextPage && !isFetchingNextPage) {
			fetchNextPage();
		}
	}, [inView, hasNextPage, isFetchingNextPage, fetchNextPage]);

	return (
		<div className="space-y-6 px-5 pb-5">
			<div>
				<h3 className="text-[16px] font-semibold text-primary">
					{t("admin.lifecycles.drawer.activity.title")}
				</h3>
				<p className="text-sm text-gray-500">
					{t("admin.lifecycles.drawer.activity.description")}
				</p>
			</div>

			<div className="bg-(--color-bg-secondary) rounded-lg p-4">
				<DetailFieldGrid>
					<DetailFieldSection>
						<ActivityField
							label={t(
								"admin.lifecycles.drawer.activity.metadata.created",
							).toUpperCase()}
							value={createdAt}
						/>
						<DetailField
							label={t(
								"admin.lifecycles.drawer.activity.metadata.createdBy",
							).toUpperCase()}
							valueClassName="font-bold text-gray-900"
							value={
								createdByUser ? (
									<span className="font-bold text-gray-900">
										{createdByUser}
									</span>
								) : (
									<EmptyValue />
								)
							}
						/>
					</DetailFieldSection>
					<DetailFieldSection>
						<ActivityField
							label={t(
								"admin.lifecycles.drawer.activity.metadata.lastUpdate",
							).toUpperCase()}
							value={updatedAt}
						/>
						<DetailField
							label={t(
								"admin.lifecycles.drawer.activity.metadata.lastUpdateBy",
							).toUpperCase()}
							valueClassName="font-bold text-gray-900"
							value={
								updatedByUser ? (
									<span className="font-bold text-gray-900">
										{updatedByUser}
									</span>
								) : (
									<EmptyValue />
								)
							}
						/>
					</DetailFieldSection>
				</DetailFieldGrid>
			</div>

			<div className="space-y-3 rounded-lg border border-gray-200 bg-white p-6">
				<div>
					<h4 className="text-[16px] font-semibold text-primary">
						{t("admin.lifecycles.drawer.activity.history.title")}
					</h4>
					<p className="text-sm text-gray-500">
						{t("admin.lifecycles.drawer.activity.history.description")}
					</p>
				</div>

				<HistoryList
					entries={historyEntries}
					isLoading={isLoading}
					emptyMessage={t("admin.lifecycles.drawer.activity.history.empty")}
					hasNextPage={hasNextPage}
					isFetchingNextPage={isFetchingNextPage}
					loadMoreRef={loadMoreRef}
					located="jobs"
				/>
			</div>
		</div>
	);
}

function ActivityField({
	label,
	value,
	includeTime,
}: {
	label: string;
	value: string | null | undefined;
	includeTime?: boolean;
}): ReactNode {
	return (
		<DetailField
			label={label}
			valueClassName="font-bold text-gray-900"
			value={
				value ? (
					<div className="flex items-center gap-2">
						<Calendar size={15} className="text-gray-400" />
						{formatLongDate(
							value,
							includeTime ? { includeTime: true } : undefined,
						)}
					</div>
				) : (
					<EmptyValue />
				)
			}
		/>
	);
}
